import type { LocalizedString } from '~/types'

/** Target text collapsed height in px */
const TARGET_TEXT_HEIGHT = 350

/**
 * Minimum height (px) that the *hidden* text content must exceed
 * before we show the ShowMoreButton.
 */
const MIN_HIDDEN_TEXT_HEIGHT = 300

/** Selectors for media elements */
const MEDIA_SELECTOR = 'img, video, iframe, .wysiwyg-media, .wysiwyg-youtube'

interface EvaluationResult {
  collapsedHeight: number
  showButton: boolean
}

/**
 * Finds the cleanest clip position (bottom of the last text line that fits within targetY)
 * where targetY is in px relative to innerEl top.
 * absoluteInnerTop is innerEl.getBoundingClientRect().top (viewport-relative).
 */
function findCleanLineClip(
  blockEl: HTMLElement,
  absoluteInnerTop: number,
  targetY: number,
): number {
  const lineRects: number[] = []

  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })

  let currentNode: Node | null = walker.nextNode()
  while (currentNode) {
    const range = document.createRange()
    range.selectNodeContents(currentNode)
    const clientRects = range.getClientRects()
    for (let i = 0; i < clientRects.length; i++) {
      const r = clientRects[i]
      if (r.height > 0 && r.width > 0) {
        // Convert viewport-relative bottom to innerEl-relative
        lineRects.push(r.bottom - absoluteInnerTop)
      }
    }
    currentNode = walker.nextNode()
  }

  if (lineRects.length === 0) return 0

  lineRects.sort((a, b) => a - b)

  let best = 0
  for (const b of lineRects) {
    // targetY is already innerEl-relative
    if (b <= targetY + 12) {
      best = Math.max(best, b)
    }
  }

  return best
}

function evaluateWysiwygExpand(innerEl: HTMLElement): EvaluationResult {
  const innerRect = innerEl.getBoundingClientRect()
  if (!innerRect.height) {
    return { collapsedHeight: TARGET_TEXT_HEIGHT, showButton: false }
  }

  const container = (innerEl.firstElementChild as HTMLElement) || innerEl
  const children = Array.from(container.children) as HTMLElement[]

  if (children.length === 0) {
    const totalH = innerEl.scrollHeight
    if (totalH > TARGET_TEXT_HEIGHT + MIN_HIDDEN_TEXT_HEIGHT) {
      return { collapsedHeight: TARGET_TEXT_HEIGHT, showButton: true }
    }
    return { collapsedHeight: totalH, showButton: false }
  }

  let accumulatedTextPx = 0
  let clipY = 0
  let cutoffIndex = -1
  let hiddenInCutoffPx = 0

  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    const isMedia =
      child.matches(MEDIA_SELECTOR) || !!child.querySelector(MEDIA_SELECTOR)

    const childTop = child.getBoundingClientRect().top - innerRect.top
    const childBottom = child.getBoundingClientRect().bottom - innerRect.top
    const childHeight = childBottom - childTop

    if (isMedia) {
      // Media elements are atomic and must never be cut off.
      // Always include full media element when accumulating.
      clipY = childBottom
      cutoffIndex = i
      hiddenInCutoffPx = 0
    } else {
      // Text element
      if (accumulatedTextPx < TARGET_TEXT_HEIGHT) {
        if (accumulatedTextPx + childHeight <= TARGET_TEXT_HEIGHT + 15) {
          accumulatedTextPx += childHeight
          clipY = childBottom
          cutoffIndex = i
          hiddenInCutoffPx = 0
        } else {
          // Text element crosses threshold -> find clean line clip
          const targetY = Math.max(TARGET_TEXT_HEIGHT, childTop + 60)
          const lineClip = findCleanLineClip(child, innerRect.top, targetY)
          if (lineClip > 0) {
            clipY = lineClip
          } else {
            clipY = childBottom
          }
          hiddenInCutoffPx = childBottom - clipY
          cutoffIndex = i
          break
        }
      } else {
        // We already have accumulated text (or text + media) reaching target height.
        // Clip inside this text element after media/text to show a snippet of text.
        const lineClip = findCleanLineClip(child, innerRect.top, childTop + 60)
        if (lineClip > 0) {
          clipY = lineClip
        } else {
          clipY = childBottom
        }
        hiddenInCutoffPx = childBottom - clipY
        cutoffIndex = i
        break
      }
    }
  }

  if (clipY === 0) {
    clipY = Math.min(innerEl.scrollHeight, TARGET_TEXT_HEIGHT)
  }

  // Evaluate remaining hidden text after cutoffIndex
  let remainingTextPx = hiddenInCutoffPx

  for (let i = cutoffIndex + 1; i < children.length; i++) {
    const child = children[i]
    const isMedia =
      child.matches(MEDIA_SELECTOR) || !!child.querySelector(MEDIA_SELECTOR)

    if (!isMedia) {
      const childTop = child.getBoundingClientRect().top - innerRect.top
      const childBottom = child.getBoundingClientRect().bottom - innerRect.top
      if (childBottom > clipY) {
        const hTop = Math.max(childTop, clipY)
        remainingTextPx += childBottom - hTop
      }
    }
  }

  // Check if cutoff child is media or text
  const lastChild = cutoffIndex >= 0 ? children[cutoffIndex] : null
  const lastIsMedia = lastChild
    ? lastChild.matches(MEDIA_SELECTOR) ||
      !!lastChild.querySelector(MEDIA_SELECTOR)
    : false

  // Show button ONLY if cutoff was inside/after a text element AND there is hidden text remaining!
  // If the last processed element is media (meaning no text followed it to clip), showButton is FALSE.
  const showButton = !lastIsMedia && remainingTextPx > MIN_HIDDEN_TEXT_HEIGHT

  const collapsedHeight = showButton
    ? Math.round(clipY + 4)
    : Math.round(innerEl.scrollHeight)

  return { collapsedHeight, showButton }
}

export function useWysiwygExpand(
  content: Ref<LocalizedString | string>,
  wrapperRef: Ref<HTMLElement | null>,
  innerRef: Ref<HTMLElement | null>,
) {
  const isExpanded = ref(false)
  const showButton = ref(false)
  const isMounted = ref(false)

  const expandedHeight = ref<number>(0)
  const collapsedHeight = ref<number>(TARGET_TEXT_HEIGHT)
  const animMaxHeight = ref<string | null>(null)

  const attachMediaListeners = () => {
    if (!innerRef.value) return
    const imgs = innerRef.value.querySelectorAll<HTMLImageElement>('img')
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => calculateHeights(), { once: true })
        img.addEventListener('error', () => calculateHeights(), { once: true })
      }
    })
  }

  const calculateHeights = () => {
    if (!innerRef.value) return

    const el = innerRef.value
    expandedHeight.value = el.scrollHeight

    attachMediaListeners()

    const {
      collapsedHeight: calculatedCollapsed,
      showButton: shouldShowButton,
    } = evaluateWysiwygExpand(el)

    collapsedHeight.value = calculatedCollapsed

    if (!isExpanded.value) {
      showButton.value = shouldShowButton
    }
  }

  watch(
    () => content.value,
    () => {
      isExpanded.value = false
      showButton.value = false
      animMaxHeight.value = null
      if (isMounted.value) {
        nextTick(() => calculateHeights())
      }
    },
  )

  let animationTimer: ReturnType<typeof setTimeout> | null = null

  watch(isExpanded, (expanded) => {
    if (animationTimer) clearTimeout(animationTimer)

    const el = innerRef.value
    if (!el) return

    if (expanded) {
      // EXPAND ANIMATION:
      // Snapshot the full height BEFORE constraining so we have a numeric target.
      const targetH = el.scrollHeight
      expandedHeight.value = targetH

      // Force the DOM to the collapsed numeric value first (so CSS can transition)
      animMaxHeight.value = `${collapsedHeight.value}px`

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          animMaxHeight.value = `${targetH}px`

          animationTimer = setTimeout(() => {
            if (isExpanded.value) {
              animMaxHeight.value = 'none'
            }
          }, 520)
        })
      })
    } else {
      // COLLAPSE ANIMATION:
      // Critical: read scrollHeight NOW (before we change animMaxHeight),
      // when the DOM still knows the full expanded height.
      // If animMaxHeight was 'none', scrollHeight gives the real full height.
      const currentH = el.scrollHeight

      // Set numeric max-height synchronously so the browser registers a
      // starting value for the CSS transition (cannot animate from 'none').
      animMaxHeight.value = `${currentH}px`

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          animMaxHeight.value = `${collapsedHeight.value}px`

          animationTimer = setTimeout(() => {
            if (!isExpanded.value) {
              animMaxHeight.value = null
              nextTick(() => calculateHeights())
            }
          }, 520)
        })
      })
    }
  })

  const contentStyle = computed(() => {
    if (!isMounted.value) return {}

    if (animMaxHeight.value !== null) {
      return { maxHeight: animMaxHeight.value }
    }

    if (!showButton.value) {
      return { maxHeight: 'none' }
    }

    return { maxHeight: `${collapsedHeight.value}px` }
  })

  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    isMounted.value = true

    nextTick(() => {
      calculateHeights()
      setTimeout(() => calculateHeights(), 200)
      setTimeout(() => calculateHeights(), 600)
    })

    if (typeof ResizeObserver !== 'undefined' && innerRef.value) {
      resizeObserver = new ResizeObserver(() => {
        calculateHeights()
      })
      resizeObserver.observe(innerRef.value)
    }
  })

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    if (animationTimer) clearTimeout(animationTimer)
  })

  return { isExpanded, showButton, contentStyle }
}
